import { zodResolver } from '@hookform/resolvers/zod';
import { ArrowLeft, Info, Loader2, Save } from 'lucide-react';
import { forwardRef, useEffect } from 'react';
import {
  useForm,
  type Control,
  type FieldPath,
  type FieldValues,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  Link,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { EquipmentPicker } from '@/components/ui/equipment-picker';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { VesselSelect } from '@/components/ui/vessel-select';
import { useEquipmentItem } from '@/features/equipment/queries';
import { useVessels } from '@/features/vessels/queries';
import { vesselDisplayName } from '@/features/vessels/types';
import {
  useCreateWorkOrder,
  useUpdateWorkOrder,
  useWorkOrder,
} from '@/features/work-orders/queries';
import {
  workOrderCreateSchema,
  workOrderUpdateSchema,
  type WorkOrderCreateValues,
  type WorkOrderUpdateValues,
} from '@/features/work-orders/schema';
import {
  WORK_ORDER_PRIORITIES,
  WORK_ORDER_TYPES,
  isWorkOrderEditable,
  type WorkOrder,
} from '@/features/work-orders/types';
import { useTaxonomy } from '@/features/taxonomy/queries';
import { ApiError } from '@/lib/api';
import { cn } from '@/lib/utils';

/**
 * WorkOrderFormPage — create/edit a Work Order using the same
 * CreateBranch + EditBranch + FormBody discriminated-union pattern
 * as the Equipment / EquipmentType forms.
 *
 * Why this pattern again here:
 *   - Create takes a wider payload (includes type, equipmentId,
 *     maintenancePlanId — all server-immutable after creation).
 *   - Update touches only the editable subset (title, description,
 *     priority, planning).
 *   - Sharing one schema via .partial() would be misleading because
 *     the backend rejects PATCH on non-draft WOs entirely (the form
 *     has nothing useful to do for those statuses).
 *
 * In edit mode we additionally check `isWorkOrderEditable(status)`
 * — only drafts proceed. Any non-draft WO renders a banner pointing
 * the user back to the detail page, since the form would be a no-op.
 */

function TextField<TForm extends FieldValues>({
  control,
  name,
  label,
  hint,
  type = 'text',
  textarea = false,
}: {
  control: Control<TForm>;
  name: FieldPath<TForm>;
  label: string;
  hint?: string;
  type?: 'text' | 'date' | 'number';
  textarea?: boolean;
}) {
  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem>
          <FormLabel>{label}</FormLabel>
          <FormControl>
            {textarea ? (
              <textarea
                {...field}
                rows={4}
                value={
                  field.value === null || field.value === undefined
                    ? ''
                    : String(field.value)
                }
                className="flex min-h-[100px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              />
            ) : (
              <Input
                type={type}
                {...field}
                value={
                  field.value === null || field.value === undefined
                    ? ''
                    : String(field.value)
                }
              />
            )}
          </FormControl>
          {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
          <FormMessage />
        </FormItem>
      )}
    />
  );
}

function ReadOnlyField({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}) {
  return (
    <div>
      <p className="text-sm font-medium">{label}</p>
      <p className="mt-1 rounded-md border bg-muted/40 px-3 py-2 text-sm">
        {value}
      </p>
    </div>
  );
}

interface CreateBranchProps {
  defaultVesselId: string | null;
  defaultEquipmentId: string | null;
}

function CreateBranch({
  defaultVesselId,
  defaultEquipmentId,
}: CreateBranchProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const createMut = useCreateWorkOrder();

  // The form holds vesselId locally (not part of the create payload —
  // the backend derives it from the chosen equipment) so the
  // EquipmentPicker can scope itself.
  const form = useForm<WorkOrderCreateValues & { _vesselId: string | null }>({
    resolver: zodResolver(workOrderCreateSchema),
    defaultValues: {
      title: '',
      description: null,
      type: 'corrective',
      priority: 'normal',
      equipmentId: defaultEquipmentId ?? '',
      plannedStartAt: null,
      dueAt: null,
      estimatedHours: null,
      maintenancePlanId: null,
      _vesselId: defaultVesselId,
    },
  });

  const errorMessage = (() => {
    if (!createMut.error) return null;
    if (createMut.error instanceof ApiError) {
      return t(createMut.error.key, {
        defaultValue: t('workOrder.form.saveError'),
        ...(createMut.error.params ?? {}),
      });
    }
    return t('workOrder.form.saveError');
  })();

  const onSubmit = form.handleSubmit((values) => {
    // Strip the local _vesselId before sending to the backend.
    const { _vesselId: _, ...payload } = values;
    void _;
    createMut.mutate(payload as WorkOrderCreateValues, {
      onSuccess: (created) => navigate(`/work-orders/${created.id}`),
    });
  });

  const watchedVesselId = form.watch('_vesselId');

  return (
    <FormBody
      mode="create"
      title={t('workOrder.form.createTitle')}
      onSubmit={onSubmit}
      submitting={createMut.isPending}
      submitLabel={t('workOrder.form.submitCreate')}
      cancelTo="/work-orders"
      errorMessage={errorMessage}
      form={form}
      watchedVesselId={watchedVesselId}
    />
  );
}

function EditBranch({ existing }: { existing: WorkOrder }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const updateMut = useUpdateWorkOrder(existing.id);
  const { data: vessels } = useVessels();
  const { data: equipment } = useEquipmentItem(existing.equipmentId);

  const initial: WorkOrderUpdateValues = {
    title: existing.title,
    description: existing.description,
    priority: existing.priority,
    plannedStartAt: existing.plannedStartAt
      ? existing.plannedStartAt.slice(0, 10)
      : null,
    dueAt: existing.dueAt ? existing.dueAt.slice(0, 10) : null,
    estimatedHours: existing.estimatedHours,
  };

  const form = useForm<WorkOrderUpdateValues>({
    resolver: zodResolver(workOrderUpdateSchema),
    defaultValues: initial,
  });

  useEffect(() => {
    form.reset(initial);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [existing.id]);

  const errorMessage = (() => {
    if (!updateMut.error) return null;
    if (updateMut.error instanceof ApiError) {
      return t(updateMut.error.key, {
        defaultValue: t('workOrder.form.saveError'),
        ...(updateMut.error.params ?? {}),
      });
    }
    return t('workOrder.form.saveError');
  })();

  const onSubmit = form.handleSubmit((values) => {
    updateMut.mutate(values, {
      onSuccess: (updated) => navigate(`/work-orders/${updated.id}`),
    });
  });

  const vesselName = vessels?.find((v) => v.id === existing.vesselId);

  return (
    <FormBody
      mode="edit"
      existing={existing}
      existingEquipmentLabel={
        equipment ? `${equipment.tag} — ${equipment.name}` : existing.equipmentId
      }
      existingVesselLabel={
        vesselName ? vesselDisplayName(vesselName) : existing.vesselId
      }
      title={t('workOrder.form.editTitle')}
      onSubmit={onSubmit}
      submitting={updateMut.isPending}
      submitLabel={t('workOrder.form.submitUpdate')}
      cancelTo={`/work-orders/${existing.id}`}
      errorMessage={errorMessage}
      form={form}
      watchedVesselId={existing.vesselId}
    />
  );
}

type FormBodyProps =
  | {
      mode: 'create';
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      form: ReturnType<typeof useForm<any>>;
      existing?: never;
      existingEquipmentLabel?: never;
      existingVesselLabel?: never;
      title: string;
      onSubmit: (e?: React.BaseSyntheticEvent) => unknown;
      submitting: boolean;
      submitLabel: string;
      cancelTo: string;
      errorMessage: string | null;
      watchedVesselId: string | null;
    }
  | {
      mode: 'edit';
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      form: ReturnType<typeof useForm<any>>;
      existing: WorkOrder;
      existingEquipmentLabel: string;
      existingVesselLabel: string;
      title: string;
      onSubmit: (e?: React.BaseSyntheticEvent) => unknown;
      submitting: boolean;
      submitLabel: string;
      cancelTo: string;
      errorMessage: string | null;
      watchedVesselId: string | null;
    };

function FormBody(props: FormBodyProps) {
  const { t } = useTranslation();
  const {
    title,
    onSubmit,
    submitting,
    submitLabel,
    cancelTo,
    errorMessage,
    mode,
    form,
    watchedVesselId,
  } = props;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const control = form.control as Control<any>;

  return (
    <div className="space-y-6">
      <header className="flex items-center gap-2">
        <Button asChild variant="ghost" size="icon" aria-label={t('common.back')}>
          <Link to={cancelTo}>
            <ArrowLeft aria-hidden />
          </Link>
        </Button>
        <h1 className="text-2xl font-semibold">{title}</h1>
      </header>

      {mode === 'edit' && (
        <Alert>
          <Info className="h-4 w-4" aria-hidden />
          <AlertDescription>{t('workOrder.form.immutableHint')}</AlertDescription>
        </Alert>
      )}

      {errorMessage && (
        <Alert variant="destructive">
          <AlertTitle>{t('workOrder.form.saveError')}</AlertTitle>
          <AlertDescription>{errorMessage}</AlertDescription>
        </Alert>
      )}

      <Form {...form}>
        <form onSubmit={onSubmit} className="space-y-4" noValidate>
          {/* Identity */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">
                {t('workOrder.detail.sectionIdentity')}
              </CardTitle>
            </CardHeader>
            <CardContent className="grid gap-4">
              <TextField
                control={control}
                name="title"
                label={t('workOrder.fields.title')}
              />
              <TextField
                control={control}
                name="description"
                label={t('workOrder.fields.description')}
                textarea
              />
            </CardContent>
          </Card>

          {/* Type & Target */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">
                {t('workOrder.detail.sectionTarget')}
              </CardTitle>
            </CardHeader>
            <CardContent className="grid gap-4 sm:grid-cols-2">
              {mode === 'create' ? (
                <>
                  <FormField
                    control={control}
                    name="type"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t('workOrder.fields.type')}</FormLabel>
                        <FormControl>
                          <WorkOrderTypeSelect {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={control}
                    name="_vesselId"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t('workOrder.fields.vesselId')}</FormLabel>
                        <FormControl>
                          <VesselSelect
                            value={field.value ?? null}
                            onChange={(v) => {
                              field.onChange(v);
                              // Clear equipmentId when vessel changes —
                              // a target on another vessel would be invalid.
                              form.setValue('equipmentId', '');
                            }}
                            allowEmpty={false}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={control}
                    name="equipmentId"
                    render={({ field }) => (
                      <FormItem className="sm:col-span-2">
                        <FormLabel>
                          {t('workOrder.fields.equipmentId')}
                        </FormLabel>
                        <FormControl>
                          <EquipmentPicker
                            vesselId={watchedVesselId}
                            value={field.value || null}
                            onChange={(v) => field.onChange(v ?? '')}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </>
              ) : (
                <>
                  <ReadOnlyField
                    label={t('workOrder.fields.type')}
                    value={t(`workOrder.type.${props.existing.type}`)}
                  />
                  <ReadOnlyField
                    label={t('workOrder.fields.vesselId')}
                    value={props.existingVesselLabel}
                  />
                  <ReadOnlyField
                    label={t('workOrder.fields.equipmentId')}
                    value={props.existingEquipmentLabel}
                  />
                </>
              )}
              <FormField
                control={control}
                name="priority"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('workOrder.fields.priority')}</FormLabel>
                    <FormControl>
                      <select
                        {...field}
                        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                      >
                        {WORK_ORDER_PRIORITIES.map((p) => (
                          <option key={p} value={p}>
                            {t(`workOrder.priority.${p}`)}
                          </option>
                        ))}
                      </select>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </CardContent>
          </Card>

          {/* Planning */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">
                {t('workOrder.detail.sectionPlanning')}
              </CardTitle>
            </CardHeader>
            <CardContent className="grid gap-4 sm:grid-cols-3">
              <TextField
                control={control}
                name="plannedStartAt"
                label={t('workOrder.fields.plannedStartAt')}
                type="date"
              />
              <TextField
                control={control}
                name="dueAt"
                label={t('workOrder.fields.dueAt')}
                type="date"
              />
              <TextField
                control={control}
                name="estimatedHours"
                label={t('workOrder.fields.estimatedHours')}
                hint={t('workOrder.fields.estimatedHoursHint')}
              />
            </CardContent>
          </Card>

          {/* Footer */}
          <div className="flex justify-end gap-2 pt-2">
            <Button asChild variant="ghost" disabled={submitting}>
              <Link to={cancelTo}>{t('common.cancel')}</Link>
            </Button>
            <Button type="submit" disabled={submitting}>
              {submitting ? (
                <Loader2 className={cn('animate-spin')} aria-hidden />
              ) : (
                <Save aria-hidden />
              )}
              {submitLabel}
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
}

/**
 * L6.1 — WO type dropdown that pulls labels from the dynamic
 * Taxonomy WORK_ORDER_TYPE catalog. Falls back to the static i18n
 * keys while the query is loading.
 */
const WorkOrderTypeSelect = forwardRef<
  HTMLSelectElement,
  React.SelectHTMLAttributes<HTMLSelectElement>
>((props, ref) => {
  const { t, i18n } = useTranslation();
  const { data: rows = [] } = useTaxonomy('WORK_ORDER_TYPE');
  const lang = (i18n.language?.split('-')[0] ?? 'en') as 'fr' | 'en' | 'es';
  const labelByCode = new Map<string, string>();
  for (const r of rows) {
    labelByCode.set(
      r.code.toLowerCase(),
      r.label[lang] || r.label.en || r.code,
    );
  }
  return (
    <select
      ref={ref}
      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      {...props}
    >
      {WORK_ORDER_TYPES.map((tp) => (
        <option key={tp} value={tp}>
          {labelByCode.get(tp) ??
            t(`workOrder.type.${tp}`, { defaultValue: tp })}
        </option>
      ))}
    </select>
  );
});
WorkOrderTypeSelect.displayName = 'WorkOrderTypeSelect';

export function WorkOrderFormPage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id);
  const [searchParams] = useSearchParams();
  const { data: existing, isLoading } = useWorkOrder(id);

  if (isEdit && isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-9 w-64" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  // Hard-stop on edit if the WO is past draft — the backend would
  // reject every PATCH so the form has nothing useful to do.
  if (isEdit && existing && !isWorkOrderEditable(existing.status)) {
    return (
      <div className="space-y-4">
        <Alert>
          <Info className="h-4 w-4" aria-hidden />
          <AlertTitle>{t('workOrder.form.notEditableTitle')}</AlertTitle>
          <AlertDescription>
            {t('workOrder.form.notEditableDescription')}
          </AlertDescription>
        </Alert>
        <Button asChild>
          <Link to={`/work-orders/${existing.id}`}>{t('common.back')}</Link>
        </Button>
      </div>
    );
  }

  if (isEdit && existing) {
    return <EditBranch existing={existing} />;
  }

  return (
    <CreateBranch
      defaultVesselId={searchParams.get('vesselId')}
      defaultEquipmentId={searchParams.get('equipmentId')}
    />
  );
}
